// apps/web/src/components/ToastStack/ToastStack.tsx
// ─────────────────────────────────────────────────────────────────────────
// THE single notification stack. Replaces GemToast + BadgeToast in AppLayout.
// Renders all three card kinds from one merged, source-tagged queue
// (useToastStack): 'gem', 'reward', and 'badge'.
//
// Behavior (per product decisions):
//   • Anchored top-right, just below the profile in MainNav. Newest on top.
//   • STAGGERED DISMISSAL: every card is assigned a FROZEN deadline on
//     entry — deadline = max(now + BASE_LIFETIME_MS, lastDeadline +
//     EXIT_STAGGER_MS). A lone toast lives its normal life; a burst leaves
//     one by one, oldest first, EXIT_STAGGER_MS apart. Deadlines never shift
//     once assigned, so the countdown ring never jumps mid-flight.
//   • COUNTDOWN RING: the ring around the close button drains over the
//     card's exact lifetime, signaling when it will leave.
//   • HOVER PAUSE: hovering a card pauses BOTH its dismiss timer and its
//     ring; leaving resumes from where they stopped, in lockstep.
//   • Exit: the card fades while sliding to the RIGHT, then the remaining
//     cards collapse upward with a small bounce (CSS owns the motion). We play
//     the exit animation locally before dispatching the real dismiss, so the
//     Redux removal happens after the card has visually left.
//   • X to dismiss: every card has a close button (manual dismiss runs the
//     same exit animation path).
//   • Respects prefers-reduced-motion via the stylesheet.
// ─────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getBadge } from '@nisk/core/badges';
import BadgeIcon from '../BadgeIcon/BadgeIcon';
import { useToastStack, type UnifiedToast } from './useToastStack';
import styles from './ToastStack.module.scss';

const BASE_LIFETIME_MS = 2200;
const EXIT_STAGGER_MS = 1000;
const EXIT_ANIM_MS = 320; // must match $exit-duration in the stylesheet

// ── Glyphs (carried over from GemToast so visuals are unchanged) ──────────
const GemGlyph: React.FC = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'>
		<path
			d='M12 2L4 9l8 13 8-13z'
			fill='currentColor'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeLinejoin='round'
		/>
	</svg>
);

const XpGlyph: React.FC = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'>
		<path
			d='M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 7.1-1.01L12 2z'
			fill='currentColor'
			stroke='currentColor'
			strokeWidth='1.2'
			strokeLinejoin='round'
		/>
	</svg>
);

const CloseGlyph: React.FC = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'>
		<path
			d='M6 6l12 12M18 6L6 18'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
		/>
	</svg>
);

// ── Per-card body renderers ──────────────────────────────────────────────
function GemBody({
	item,
}: {
	item: Extract<UnifiedToast, { source: 'economy' }>['item'];
}) {
	if (item.kind === 'gem') {
		return (
			<>
				<span
					className={styles.icon}
					aria-hidden='true'>
					<GemGlyph />
				</span>
				<div className={styles.body}>
					<span className={styles.amount}>+{item.amount}</span>
					{item.reason && (
						<span className={styles.reason}>{item.reason}</span>
					)}
				</div>
			</>
		);
	}

	// kind === 'reward' — combined XP + Gems
	return (
		<>
			<span
				className={`${styles.icon} ${styles.iconXp}`}
				aria-hidden='true'>
				<XpGlyph />
			</span>
			<div className={styles.body}>
				<span className={styles.headline}>{item.headline}</span>
				<div className={styles.lines}>
					{item.xp > 0 && (
						<span className={styles.xpLine}>+{item.xp} XP</span>
					)}
					{item.gems > 0 && (
						<span className={styles.gemLine}>
							<span
								className={styles.gemMini}
								aria-hidden='true'>
								<GemGlyph />
							</span>
							+{item.gems}
						</span>
					)}
				</div>
				{item.leveledUp && (
					<span className={styles.levelUp}>
						Level up
						{item.levelAfter ? ` · Lv ${item.levelAfter}` : ''}
					</span>
				)}
			</div>
		</>
	);
}

function BadgeBody({
	item,
}: {
	item: Extract<UnifiedToast, { source: 'badges' }>['item'];
}) {
	const def = getBadge(item.badgeId);
	const major = item.weight === 'major';
	return (
		<>
			<span
				className={styles.icon}
				aria-hidden='true'>
				{def && <BadgeIcon kind={def.icon} />}
			</span>
			<div className={styles.body}>
				<span className={styles.eyebrow}>
					{major ? 'Badge unlocked' : 'Badge earned'}
				</span>
				<span className={styles.name}>{item.name}</span>
				<span className={styles.desc}>{item.description}</span>
				{major && item.nextAction && (
					<span className={styles.next}>Next: {item.nextAction}</span>
				)}
			</div>
		</>
	);
}

// ── Single card: owns its lifetime timer + exit animation ────────────────
interface CardProps {
	toast: UnifiedToast;
	/** Frozen wall-clock deadline (ms epoch), assigned once by the stack. */
	deadline: number;
	onDismiss: (t: UnifiedToast) => void;
}

const ToastCard: React.FC<CardProps> = ({ toast, deadline, onDismiss }) => {
	const [exiting, setExiting] = useState(false);
	const [paused, setPaused] = useState(false);
	const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Begin exit: play the fade-right animation, then dispatch the real
	// removal after the animation finishes. Guarded so it runs once.
	const beginExit = useCallback(() => {
		if (exiting) return;
		setExiting(true);
		exitTimer.current = setTimeout(() => onDismiss(toast), EXIT_ANIM_MS);
	}, [exiting, onDismiss, toast]);

	// Frozen lifetime: captured ONCE from the assigned deadline, then owned by
	// remainingRef. Hover pause clears the timer and banks the remaining time;
	// unhover restarts from the banked value. The ring's CSS animation is
	// paused/resumed in lockstep, so both always agree.
	const [initialLife] = useState(() =>
		Math.max(600, deadline - Date.now()),
	);
	const remainingRef = useRef(initialLife);

	useEffect(() => {
		if (exiting || paused) return undefined;
		const startedAt = Date.now();
		const t = setTimeout(beginExit, Math.max(0, remainingRef.current));
		return () => {
			clearTimeout(t);
			remainingRef.current = Math.max(
				0,
				remainingRef.current - (Date.now() - startedAt),
			);
		};
	}, [beginExit, exiting, paused]);

	useEffect(
		() => () => {
			if (exitTimer.current) clearTimeout(exitTimer.current);
		},
		[],
	);

	const major = toast.source === 'badges' && toast.item.weight === 'major';
	const reward = toast.source === 'economy' && toast.item.kind === 'reward';

	const cardClass = [
		styles.toast,
		major ? styles.toastMajor : '',
		reward ? styles.toastReward : '',
		exiting ? styles.exiting : '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div
			className={cardClass}
			role='status'
			aria-live='polite'
			onMouseEnter={() => setPaused(true)}
			onMouseLeave={() => setPaused(false)}>
			{toast.source === 'economy' ? (
				<GemBody item={toast.item} />
			) : (
				<BadgeBody item={toast.item} />
			)}
			{/* Close + countdown ring: drains once over the frozen lifetime.
			    No key/restart — the deadline never moves; hover only pauses. */}
			<span className={styles.closeWrap}>
				{!exiting && (
					<svg
						className={styles.ring}
						viewBox='0 0 26 26'
						aria-hidden='true'>
						<circle
							className={styles.ringTrack}
							cx='13'
							cy='13'
							r='11'
						/>
						<circle
							className={`${styles.ringFill} ${
								paused ? styles.ringPaused : ''
							}`}
							cx='13'
							cy='13'
							r='11'
							style={{ animationDuration: `${initialLife}ms` }}
						/>
					</svg>
				)}
				<button
					type='button'
					className={styles.close}
					aria-label='Dismiss notification'
					onClick={beginExit}>
					<CloseGlyph />
				</button>
			</span>
		</div>
	);
};

// ── The stack ─────────────────────────────────────────────────────────────
const ToastStack: React.FC = () => {
	const { visible, dismiss } = useToastStack();

	// Frozen staggered deadlines: each card gets ONE deadline the first time
	// it appears — at least a full base lifetime from now, and at least one
	// stagger step after the previous card's deadline. Bursts therefore leave
	// oldest-first, one step apart, and nothing recomputes afterwards.
	const deadlines = useRef(new Map<string, number>());
	const lastDeadline = useRef(0);

	if (visible.length === 0) {
		// Reset bookkeeping when the stack fully clears so an old deadline
		// can't push a future burst unnecessarily far out.
		if (deadlines.current.size > 0) deadlines.current.clear();
		lastDeadline.current = 0;
		return null;
	}

	for (const t of visible) {
		const key = `${t.source}:${t.id}`;
		if (!deadlines.current.has(key)) {
			const d = Math.max(
				Date.now() + BASE_LIFETIME_MS,
				lastDeadline.current + EXIT_STAGGER_MS,
			);
			deadlines.current.set(key, d);
			lastDeadline.current = d;
		}
	}

	// Render newest-on-top (top-right anchor). `visible` is oldest→newest, so
	// column-reverse in CSS puts the newest at the top.
	return (
		<div
			className={styles.container}
			aria-live='polite'>
			{visible.map((t) => {
				const key = `${t.source}:${t.id}`;
				return (
					<ToastCard
						key={key}
						toast={t}
						deadline={deadlines.current.get(key) ?? Date.now() + BASE_LIFETIME_MS}
						onDismiss={(toast) => {
							deadlines.current.delete(key);
							dismiss(toast);
						}}
					/>
				);
			})}
		</div>
	);
};

export default ToastStack;
